// @ts-strict-ignore
import { useEffect, useMemo } from "react";

import { useWholesaleCartQuery } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { MultiCartType } from "@shared/types/MultiCartType";

import { CartBody } from "./CartBody/CartBody";
import { EmptyCart } from "./EmptyCart/EmptyCart";
import { SkeletonCart } from "./SkeletonCart/SkeletonCart";

import * as styles from "./EmersonCartPopup.styles";

const EmersonCartPopup = () => {
  const logAnalyticsEvent = useAnalyticsLogger();

  const { data: wholesaleCartData, loading: loadingWholesaleCart } = useWholesaleCartQuery({
    fetchPolicy: "cache-and-network",
  });

  const { itemTotal: subtotal, lineItems } = wholesaleCartData?.currentOffice?.wholesaleCart || {
    subtotal: 0,
    lineItems: [],
  };

  const numberOfCartItems = useMemo(() => {
    return lineItems.map(item => item.quantity).reduce((sum: number, value) => sum + value, 0);
  }, [lineItems]);

  useEffect(() => {
    if (!loadingWholesaleCart) {
      const cart = wholesaleCartData?.currentOffice?.wholesaleCart;
      logAnalyticsEvent({
        eventName: AnalyticsEvent.EMERSON_CHECKOUT_VIEW_CART_DROPDOWN,
        payload: {
          number_of_items: numberOfCartItems,
          number_of_products: cart?.lineItems?.length || 0,
          order_id: cart?.id,
          multicart_type: cart?.isCreatedByPractitioner
            ? MultiCartType.CUSTOMER
            : MultiCartType.ADMIN,
        },
      });
    }
  }, [loadingWholesaleCart, numberOfCartItems]);

  const renderCartBody = () => {
    if (loadingWholesaleCart) return <SkeletonCart />;
    if (numberOfCartItems === 0) {
      return <EmptyCart />;
    } else {
      return (
        <CartBody
          numberOfCartItems={numberOfCartItems}
          subtotal={subtotal}
          cartData={wholesaleCartData}
        />
      );
    }
  };

  return (
    <div data-testid="emerson-cart-body" css={styles.cartPopup}>
      {renderCartBody()}
    </div>
  );
};

export { EmersonCartPopup };
